import * as THREE from 'three';

export const DEFAULT_SCENE_LOOK = 'studio';

const TONE = {
  aces: THREE.ACESFilmicToneMapping,
  none: THREE.NoToneMapping,
  reinhard: THREE.ReinhardToneMapping,
  cineon: THREE.CineonToneMapping,
};

/**
 * Lighting / renderer looks for the 3D scene.
 * Directional light positions are directions only (infinite lights).
 */
export const SCENE_LOOKS = [
  {
    id: 'studio',
    label: 'Studio',
    group: 'Realistic',
    description: 'Soft 3-point light with gentle studio reflections. Even, natural, and easy on the eyes.',
    toneMapping: 'aces',
    exposure: 1.08,
    roomEnv: true,
    envIntensity: 0.32,
    headlight: 0.18,
    hemisphere: { sky: '#c8d6ea', ground: '#3e372e', intensity: 0.48 },
    ambient: { color: '#ffffff', intensity: 0.2 },
    directional: [
      { position: [120, 180, 55], intensity: 1.12, color: '#fff1dc' },
      { position: [-95, 70, -85], intensity: 0.38, color: '#c7dcff' },
      { position: [-35, 95, 150], intensity: 0.58, color: '#fffaf2' },
    ],
  },
  {
    id: 'daylight',
    label: 'Daylight',
    group: 'Realistic',
    description: 'Sun from above plus cool sky bounce. Closest to outdoor daylight on rock and steel.',
    toneMapping: 'aces',
    exposure: 1.12,
    roomEnv: true,
    envIntensity: 0.28,
    headlight: 0.1,
    hemisphere: { sky: '#8eb8e8', ground: '#6a5844', intensity: 0.72 },
    ambient: { color: '#eef4ff', intensity: 0.14 },
    directional: [
      { position: [140, 220, 90], intensity: 1.45, color: '#fff3c8' },
      { position: [-80, 25, -50], intensity: 0.28, color: '#b4c8e6' },
    ],
  },
  {
    id: 'overcast',
    label: 'Overcast',
    group: 'Realistic',
    description: 'Cloudy-day wrap light. Cuts hot specular spots so colour and shape stay readable.',
    toneMapping: 'aces',
    exposure: 1.02,
    roomEnv: true,
    envIntensity: 0.18,
    headlight: 0.12,
    hemisphere: { sky: '#d5dde6', ground: '#5a534c', intensity: 0.95 },
    ambient: { color: '#ffffff', intensity: 0.32 },
    directional: [
      { position: [40, 200, 20], intensity: 0.55, color: '#f4f7fb' },
      { position: [-60, 80, -90], intensity: 0.28, color: '#dce6f2' },
    ],
  },
  {
    id: 'mine',
    label: 'Mine lamps',
    group: 'Realistic',
    description: 'Warm underground lamps with cool bounce. Suits stopes, drives, and dark rock.',
    toneMapping: 'aces',
    exposure: 0.98,
    roomEnv: true,
    envIntensity: 0.22,
    headlight: 0.28,
    hemisphere: { sky: '#6a645c', ground: '#2a2218', intensity: 0.38 },
    ambient: { color: '#ffd9a8', intensity: 0.18 },
    directional: [
      { position: [55, 110, -40], intensity: 1.05, color: '#ffd29a' },
      { position: [-90, 35, 70], intensity: 0.48, color: '#9bb6d4' },
      { position: [20, 55, 120], intensity: 0.42, color: '#ffe8c4' },
    ],
  },
  {
    id: 'pop',
    label: 'Bright pop',
    group: 'Game',
    description: 'Punchy key + gold rim so solids pop off the background. Best all-round “beautiful” look.',
    toneMapping: 'aces',
    exposure: 1.18,
    roomEnv: true,
    envIntensity: 0.2,
    headlight: 0.32,
    hemisphere: { sky: '#a7c6ec', ground: '#4a4038', intensity: 0.62 },
    ambient: { color: '#ffffff', intensity: 0.38 },
    directional: [
      { position: [90, 140, 50], intensity: 1.28, color: '#ffffff' },
      { position: [-100, 75, -55], intensity: 0.72, color: '#c5dcff' },
      { position: [10, 110, -160], intensity: 1.15, color: '#ffe4b0' },
    ],
  },
  {
    id: 'neon',
    label: 'Neon rim',
    group: 'Game',
    description: 'Teal and amber rim lights, game-cinematic silhouette. Objects read clearly from any angle.',
    toneMapping: 'aces',
    exposure: 1.16,
    roomEnv: true,
    envIntensity: 0.16,
    headlight: 0.22,
    hemisphere: { sky: '#3d5a80', ground: '#1a1520', intensity: 0.36 },
    ambient: { color: '#1c2433', intensity: 0.28 },
    directional: [
      { position: [75, 110, 40], intensity: 1.05, color: '#fff8f0' },
      { position: [-110, 55, 85], intensity: 1.25, color: '#3fe0d2' },
      { position: [95, 40, -120], intensity: 0.95, color: '#ff8c42' },
    ],
  },
  {
    id: 'cinema',
    label: 'Cinematic',
    group: 'Game',
    description: 'Dramatic contrast: strong key, cool rim, darker fill. Presentation / screenshot look.',
    toneMapping: 'aces',
    exposure: 0.92,
    roomEnv: true,
    envIntensity: 0.24,
    headlight: 0.08,
    hemisphere: { sky: '#4a5870', ground: '#1a1814', intensity: 0.22 },
    ambient: { color: '#ffffff', intensity: 0.1 },
    directional: [
      { position: [130, 90, 30], intensity: 1.7, color: '#ffeedd' },
      { position: [-70, 45, -110], intensity: 1.15, color: '#88c0ff' },
      { position: [-40, 30, 40], intensity: 0.18, color: '#6a7a90' },
    ],
  },
  {
    id: 'survey',
    label: 'High vis',
    group: 'Technical',
    description: 'Bright wrap + camera fill. Grade colours and thin solids stay visible with almost no glare.',
    toneMapping: 'reinhard',
    exposure: 1.05,
    roomEnv: false,
    envIntensity: 0,
    headlight: 0.55,
    hemisphere: { sky: '#e8eef6', ground: '#8a847c', intensity: 0.7 },
    ambient: { color: '#ffffff', intensity: 0.72 },
    directional: [
      { position: [80, 160, 80], intensity: 0.7, color: '#ffffff' },
      { position: [-80, 120, -80], intensity: 0.7, color: '#ffffff' },
      { position: [0, 40, 120], intensity: 0.35, color: '#f0f4ff' },
    ],
  },
  {
    id: 'classic',
    label: 'Classic',
    group: 'Technical',
    description: 'Original overhead pair. Keep this if you preferred the previous lighting.',
    toneMapping: 'none',
    exposure: 1,
    roomEnv: false,
    envIntensity: 0,
    headlight: 0,
    hemisphere: null,
    ambient: { color: '#ffffff', intensity: 2 },
    directional: [
      { position: [1, 200, -300], intensity: 2.5, color: '#ffffff' },
      { position: [1, 200, 300], intensity: 2.5, color: '#ffffff' },
      { position: [5, 5, 5], intensity: 0.5, color: '#ffffff' },
    ],
  },
];

export const SCENE_LOOK_GROUPS = ['Realistic', 'Game', 'Technical'];

export function resolveSceneLook(id) {
  const found = SCENE_LOOKS.find((look) => look.id === id);
  return found || SCENE_LOOKS.find((look) => look.id === DEFAULT_SCENE_LOOK) || SCENE_LOOKS[0];
}

export function looksInGroup(group) {
  return SCENE_LOOKS.filter((look) => look.group === group);
}

export function toneMappingForLook(look) {
  return TONE[look?.toneMapping] ?? THREE.ACESFilmicToneMapping;
}
